import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { ShieldCheck, BrainCircuit, Sparkles, FileText, ArrowRight, Moon, Users, Bell } from "lucide-react";
import { RecommendationsPanel } from "@/components/RecommendationsPanel";
import { ConsultationBooking } from "@/components/ConsultationBooking";

const STARS = Array.from({ length: 40 }, () => {
  const top = Math.random() * 100;
  const left = Math.random() * 100;
  return {
    id: `s-${top.toFixed(3)}-${left.toFixed(3)}`,
    top, left,
    size: Math.random() * 2.2 + 1, delay: Math.random() * 4, duration: 3 + Math.random() * 4,
  };
});

function StarField() {
  return (
    <div className="starfield" aria-hidden>
      {STARS.map((s) => (
        <span key={s.id} className="star" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: `${s.size}px`, height: `${s.size}px`,
          animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
        }} />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState({ matches: 0, notifications: 0 });
  const [readiness, setReadiness] = useState(null);

  // Mount-only fetch: `refresh` is stable (useCallback[]); no stale-closure risk.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, n, r] = await Promise.all([
          api.get("/matches"),
          api.get("/notifications"),
          api.get("/readiness-score").catch(() => null),
        ]);
        if (!alive) return;
        setStats({ matches: (m.data.matches || []).length, notifications: (n.data.notifications || []).length });
        if (r?.data) setReadiness(r.data);
      } catch {
        // Silent — dashboard degrades gracefully with default zeros.
      }
    })();
    refresh();
    return () => { alive = false; };
  }, [refresh]);

  if (!user) return null;
  const kycOk = user.kyc_status === "verified";
  const assessDone = user.assessment_completed;
  const tier = user.subscription_tier || "basic";
  const tierLabel = tier === "vip" ? "VIP" : tier;

  const steps = [
    { done: !!(user.profile?.city && user.profile?.profession), title: "Complete your biodata", link: "/profile", icon: FileText },
    { done: kycOk, title: "Verify identity (KYC)", link: "/kyc", icon: ShieldCheck },
    { done: assessDone, title: "Marriage Intelligence Assessment", link: assessDone ? "/results" : "/assessment", icon: BrainCircuit },
    { done: stats.matches > 0, title: "Review your curated matches", link: "/matches", icon: Sparkles },
  ];
  const nextStep = steps.find(s => !s.done) || steps[3];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Celestial header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="rounded-[2rem] p-8 lg:p-12 relative overflow-hidden" style={{ background: "#12103A", color: "white" }}
        >
          <StarField />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <div className="devanagari text-amber-300 text-sm">नमस्ते</div>
              <div className="overline mt-2" style={{ color: "#FCD34D" }}>Your Anubandh journey</div>
              <h1 className="font-serif-display text-4xl lg:text-5xl mt-3 leading-[1.05]">
                Namaste, {user.profile?.full_name || user.full_name}.
              </h1>
              <p className="mt-4 text-stone-300 max-w-xl">
                One thoughtful step at a time. No endless browsing. The stars are quiet — the intelligence is speaking.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-xs">
                <div className={`rounded-full px-3 py-1.5 border ${assessDone ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-white/20 text-stone-300"}`}>
                  <span className="mr-1">✦</span>Assessment {assessDone ? "complete" : "pending"}
                </div>
                <div className={`rounded-full px-3 py-1.5 border ${kycOk ? "border-emerald-400 text-emerald-300 bg-emerald-500/10" : "border-white/20 text-stone-300"}`}>
                  <span className="mr-1">✦</span>KYC {kycOk ? "verified" : "pending"}
                </div>
                <div className="rounded-full px-3 py-1.5 border border-amber-400 text-amber-300 bg-amber-500/10 capitalize">
                  <span className="mr-1">✦</span>{tierLabel} plan
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              {readiness && (
                <Link to="/readiness" data-testid="dashboard-readiness-card" className="rounded-2xl p-5 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="overline" style={{ color: "#FCD34D" }}>Readiness Score</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <div className="font-serif-display text-4xl">{readiness.overall}</div>
                    <div className="text-sm text-amber-300">{readiness.level}</div>
                  </div>
                  <div className="text-xs text-stone-400 mt-1">Tap to explore →</div>
                </Link>
              )}
              <Link to={nextStep.link} data-testid="dashboard-next-cta"
                className="rounded-2xl p-5 bg-amber-500 text-[#12103A] hover:bg-amber-400 transition-colors">
                <div className="overline text-[#12103A]/70">Next best action</div>
                <div className="font-serif-display text-lg mt-1 leading-snug">{nextStep.title}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-sm font-medium">Continue <ArrowRight size={14} /></div>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Content grid */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-stone-200 rounded-2xl bg-white p-6">
              <div className="flex items-center justify-between">
                <div className="overline">Your journey</div>
                <Link to="/matches" className="text-xs text-amber-700 underline-grow">View matches →</Link>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {steps.map((s) => (
                  <div key={s.title} className={`border rounded-xl p-4 flex items-center gap-3 ${s.done ? "bg-amber-50 border-amber-200" : "border-stone-200 bg-[#F9F8F6]"}`}>
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-xs shrink-0 ${s.done ? "bg-amber-500 text-white" : "bg-white border border-stone-300 text-stone-400"}`}>
                      {s.done ? "✓" : <s.icon size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1E1B4B] font-medium truncate">{s.title}</div>
                      <Link to={s.link} className="text-xs text-stone-500 hover:text-[#1E1B4B]">Open →</Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* My Recommendations — weekly automated matches */}
            <RecommendationsPanel />

            {/* My Assessment Results — shown once assessment is complete */}
            {assessDone && (
              <div
                data-testid="dashboard-results-card"
                className="rounded-2xl p-6 relative overflow-hidden border border-indigo-200"
                style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #F9F8F6 100%)" }}
              >
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <BrainCircuit className="text-[#1E1B4B]" />
                    <div className="overline mt-3">Assessment Insight</div>
                    <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-2">Your results are ready.</h3>
                    <p className="mt-2 text-sm text-stone-700 max-w-xl">
                      Overall score, category breakdown (Lifestyle · Values · Growth), personalised analysis, and next-step recommendations.
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex gap-3 flex-wrap">
                  <Link
                    to="/results"
                    data-testid="dashboard-view-results"
                    className="btn-pill text-sm text-white px-5 py-2.5 flex items-center gap-1"
                    style={{ background: "#1E1B4B" }}
                  >
                    View My Assessment Results <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/readiness"
                    className="btn-pill text-sm text-[#1E1B4B] border border-stone-300 px-5 py-2.5 hover:bg-white"
                  >
                    Readiness Score
                  </Link>
                </div>
              </div>
            )}

            {/* Kundali teaser card */}
            <div className="rounded-2xl p-6 relative overflow-hidden border border-amber-200" style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #F9F8F6 100%)" }}>
              <Moon className="text-amber-700" />
              <div className="overline mt-3">Kundali × Intelligence Fusion</div>
              <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-2">Where Ashtakoot meets algorithm.</h3>
              <p className="mt-2 text-sm text-stone-700 max-w-xl">
                Add your date & time of birth to your profile to unlock Kundali fusion on every match report — Vedic Guna Milan blended with AI compatibility.
              </p>
              <div className="mt-5 flex gap-3">
                <Link to="/profile" data-testid="dashboard-kundali-profile" className="btn-pill text-sm text-white px-5 py-2.5" style={{ background: "#1E1B4B" }}>Add birth details</Link>
                <Link to="/matches" className="btn-pill text-sm text-[#1E1B4B] border border-stone-300 px-5 py-2.5 hover:bg-white">See matches</Link>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <ConsultationBooking />

            <div className="border border-stone-200 rounded-2xl bg-white p-6">
              <div className="overline">This week</div>
              <div className="mt-3 flex items-baseline gap-2">
                <Sparkles size={18} className="text-amber-700" />
                <span className="font-serif-display text-3xl text-[#1E1B4B]">{stats.matches}</span>
                <span className="text-sm text-stone-500">curated matches</span>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <Bell size={18} className="text-amber-700" />
                <span className="font-serif-display text-3xl text-[#1E1B4B]">{stats.notifications}</span>
                <span className="text-sm text-stone-500">notifications</span>
              </div>
            </div>

            <div className="border border-stone-200 rounded-2xl bg-white p-6">
              <div className="overline">Plan</div>
              <div className="font-serif-display text-2xl text-[#1E1B4B] mt-2 capitalize">{tierLabel}</div>
              {["basic", "free"].includes(tier) && (
                <Link to="/subscription" data-testid="dashboard-upgrade-cta" className="btn-pill inline-flex mt-4 text-white text-sm px-5 py-2.5" style={{ background: "#D97706" }}>Upgrade to Premium</Link>
              )}
              {!["basic", "free"].includes(tier) && (
                <Link to="/subscription" className="btn-pill inline-flex mt-4 text-[#1E1B4B] border border-stone-300 text-sm px-5 py-2.5 hover:bg-stone-50">Manage plan</Link>
              )}
            </div>

            <div className="border border-white/10 rounded-2xl p-6 relative overflow-hidden text-white" style={{ background: "#12103A" }}>
              <Users size={20} className="text-amber-300" />
              <div className="overline mt-3" style={{ color: "#FCD34D" }}>Family Circle</div>
              <p className="font-serif-display text-lg mt-2 leading-snug">&ldquo;The decision is yours. The blessing is theirs.&rdquo;</p>
              <p className="text-xs text-stone-400 mt-3">Available on Journey — invite up to 4 family members to view your reports.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
